import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useToast } from '@morressier/alembic';
import { zodResolver } from '@hookform/resolvers/zod';
import { Overlay } from 'src/components/Overlay';
import AddExperts from './AddExperts';
import useCurrentContextId from 'src/hooks/useCurrentContextId';
import { ICommandResponse, useInviteExperts } from 'src/api/commands';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import ConfirmInviteExitModal from '../../ReviewTab/InviteReviewer/ConfirmInviteExitModal';
import { IInviteExpertsForm } from './commonTypes';
import AddContextToInvitation from './AddContextToInvitation';
import ConfirmInviteBoardMemberModal from './ConfirmInviteBoardMemberModal';
import {
  emailContentSchema,
  inviteExpertsSchema
} from '@publishing-engine/domain/validation';
import { IEarlyAssessmentEditorialRequest } from '@publishing-engine/domain/models';
import { EmailContentForm } from '../../../EmailModule';
import { IEmailCommandPayload } from '@publishing-engine/domain/types/email-template';
import ComposeExpertInvitation from './ComposeExpertInvitation';
import { normalizeRecipients } from '../../../../utils/normalizeEmailRecipients';
import { useAttachmentSync } from 'src/hooks/useAttachmentSync';

interface Props {
  closeModal: () => void;
}

const steps = [AddExperts, AddContextToInvitation, ComposeExpertInvitation];

const InviteExpertsModal = ({ closeModal }: Props) => {
  // state
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [showExitConfirmModal, setShowExitConfirmModal] =
    useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  // hooks
  const toast = useToast();
  const submissionContextId = useCurrentContextId() ?? '';
  const { availableActions, submissionId, organizationId } =
    useSubmissionData();
  const totalSteps = steps.length;

  const expertMethods = useForm<IInviteExpertsForm>({
    defaultValues: {
      experts: [{ id: '', name: '', email: '' }],
      addContext: {
        attachments: [],
        comment: ''
      }
    },
    resolver: zodResolver(inviteExpertsSchema)
  });

  const {
    formState: { errors, isDirty },
    reset
  } = expertMethods;

  const emailContentMethods = useForm<EmailContentForm>({
    defaultValues: {
      emailContent: {
        body: '',
        subject: '',
        recipients: { to: [], cc: [], bcc: [] },
        attachments: []
      }
    },
    resolver: zodResolver(emailContentSchema)
  });

  // Sync attachments between step 2 and step 3
  useAttachmentSync({
    sourceForm: expertMethods,
    targetForm: emailContentMethods,
    sourceField: 'addContext.attachments',
    targetField: 'emailContent.attachments',
    transformToTarget: (files: any[]) => {
      if (!Array.isArray(files)) return [];
      return files.map(file => ({
        id: file.fileId,
        referenceName: file.referenceName,
        name: file.name,
        extension: file.extension,
        url: file.url
      }));
    },
    transformToSource: (files: any[]) => {
      if (!Array.isArray(files)) return [];
      return files.map(file => ({
        fileId: file.id,
        referenceName: file.referenceName,
        name: file.name,
        extension: file.extension,
        url: file.url,
        isUploading: false,
        progress: 0
      }));
    }
  });

  const Component = steps[currentStep - 1];

  if (!Component) return null;

  const goToNextStep = () => {
    setCurrentStep(currentStep + 1);
  };

  const goToPreviousStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleCloseModal = () => {
    // show exit confirmation modal
    if (isDirty) {
      setShowExitConfirmModal(true);
    } else {
      reset();
      closeModal();
    }
  };

  const handleExit = () => {
    setShowExitConfirmModal(false);
    closeModal();
    reset();
  };
  const reviewListKey = [
    'board-member-experts-invite-list',
    { submissionContextId, submissionId }
  ];

  const inviteReviewerMutation = useInviteExperts(
    submissionId,
    submissionContextId,
    reviewListKey,
    'RequestEarlyAssessment',
    organizationId
  );

  const handleReviewerInvite = async (
    experts: { id: string; email: string }[],
    newExpertsAddToContext: IEarlyAssessmentEditorialRequest
  ) => {
    const results: ICommandResponse[] = [];
    if (availableActions.contains('RequestEarlyAssessment')) {
      const { getValues } = emailContentMethods;
      const baseEmailContext = getValues('emailContent');
      for (const { id, email } of experts) {
        const emails: IEmailCommandPayload = [
          {
            ...baseEmailContext,
            recipients: {
              ...normalizeRecipients(baseEmailContext.recipients),
              to: [email]
            }
          }
        ];
        results.push(
          await inviteReviewerMutation.mutateAsync({
            id: id,
            editorialRequest: newExpertsAddToContext,
            emails
          })
        );
      }
    }
    const successCount = results.filter(result => result.isSuccess).length;

    const rejectedCount = results.filter(result => !result.isSuccess).length;

    if (rejectedCount) {
      toast({
        variant: 'error',
        title: 'Invites Failed',
        description: `${rejectedCount} Experts Failed to Invite, Please try again.`,
        isClosable: true
      });
    }

    return successCount;
  };
  const handleInviteExperts = async () => {
    if (errors.experts?.length || isProcessing) {
      return;
    }

    setIsProcessing(true);

    // @NOTE: This is a temporary solution to invite reviewers, until we work on add reviewer to queue
    // this will make sure that the reviewers are being invited using new UI
    const newExperts = expertMethods.getValues('experts');
    const newExpertsAddToContext = expertMethods.getValues('addContext');
    let noOfInvitedReviewers = 0;
    if (newExperts.length) {
      const inviteExperts = newExperts.map(boardMember => {
        return { id: boardMember.id, email: boardMember.email };
      });
      noOfInvitedReviewers = await handleReviewerInvite(
        inviteExperts,
        newExpertsAddToContext
      );
    }

    toast({
      variant: 'success',
      title: `${noOfInvitedReviewers} Experts Invited.`,
      description:
        'Experts will be emailed an invitation to support on this Submission.',
      isClosable: true
    });

    setIsProcessing(false);
    setShowConfirmModal(false);
    closeModal();
  };

  const handleStepRender = () => {
    const Component = steps[currentStep - 1];
    if (!Component) return null;

    switch (currentStep) {
      case 1:
        return (
          <FormProvider {...expertMethods}>
            <Component
              currentStep={currentStep}
              totalSteps={totalSteps}
              goToNextStep={goToNextStep}
              goToPreviousStep={goToPreviousStep}
              closeModal={handleCloseModal}
            />
          </FormProvider>
        );
      case 2:
        return (
          <FormProvider {...expertMethods}>
            <Component
              currentStep={currentStep}
              totalSteps={totalSteps}
              goToNextStep={goToNextStep}
              goToPreviousStep={goToPreviousStep}
              closeModal={handleCloseModal}
            />
          </FormProvider>
        );
      case 3:
        return (
          <FormProvider {...emailContentMethods}>
            <Component
              currentStep={currentStep}
              totalSteps={totalSteps}
              goToNextStep={goToNextStep}
              goToPreviousStep={goToPreviousStep}
              closeModal={handleCloseModal}
              emails={expertMethods
                .getValues('experts')
                .map(expert => expert.email)}
              // Pass the initial attachments from step 2
              // The rest of the syncing is done with the hook above.
              attachments={
                expertMethods
                  .getValues('addContext')
                  .attachments?.map(file => ({
                    id: file.fileId || file.id,
                    referenceName: file.referenceName,
                    name: file.name,
                    extension: file.extension,
                    url: file.url
                  })) || []
              }
            />
          </FormProvider>
        );
      default:
        return null;
    }
  };

  return (
    <Overlay>
      <FormProvider {...expertMethods}>
        {showConfirmModal && (
          <ConfirmInviteBoardMemberModal
            isProcessing={isProcessing}
            onClose={() => setShowConfirmModal(false)}
            onInviteExperts={handleInviteExperts}
          />
        )}
        {showExitConfirmModal && (
          <ConfirmInviteExitModal
            onClose={handleExit}
            onCancel={() => setShowExitConfirmModal(false)}
          />
        )}
      </FormProvider>
      {handleStepRender()}
    </Overlay>
  );
};

export default InviteExpertsModal;
